import type { AxiosResponse } from "axios";
import axios from "axios";
import type { InferType } from "yup";
import * as yup from "yup";

import api from "../utils/AxiosInterceptor";
import { removeNotNumbers } from "../utils/removeNotNumbers";
import i18n from "../i18n";

export interface ResponseRegister {
  user: {
    id: number;
    email: string;
    name: string;
  };
}

export interface DataCheckedCpf {
  value: string;
  exists: boolean;
}

export interface CheckEmailExist {
  value: string;
  exists: boolean;
}

export const registerSchemaInput = yup.object({
  name: yup
    .string()
    .validateFullName()
    .required(() => i18n.t("validation.fullNameRequired")),
  email: yup
    .string()
    .validateEmail()
    .email(() => i18n.t("validation.emailInvalid"))
    .required(() => i18n.t("validation.emailRequired")),
  confirm_email: yup
    .string()
    .oneOf([yup.ref("email")], () => i18n.t("validation.emailsMustMatch"))
    .when("email", (email, schema) => {
      if (email) {
        return schema
          .required(() => i18n.t("validation.confirmEmailRequired"))
          .oneOf([yup.ref("email")], () => i18n.t("validation.emailsMustMatch"));
      }
      return schema.notRequired();
    })
    .required(() => i18n.t("validation.confirmEmailRequired")),
  cep: yup
    .string()
    .required(() => i18n.t("register.cepRequired"))
    .min(10, () => i18n.t("register.cepDigits")),
  state: yup.string(),
  city: yup.string(),
  neighborhood: yup.string(),
  street: yup.string(),
  streetNumber: yup.string().required(() => i18n.t("validation.requiredField")),
  complement: yup.string(),
  password: yup
    .string()
    .validatePassword()
    .required(() => i18n.t("validation.requiredField")),
  confirmation_password: yup
    .string()
    .required(() => i18n.t("validation.confirmPasswordRequired"))
    .oneOf([yup.ref("password")], () => i18n.t("validation.passwordsMustMatch")),
  phone: yup.string(),
  cellphone: yup
    .string()
    .required(() => i18n.t("validation.cellphoneRequired"))
    .matches(
      /^\(\d{2}\) \d \d{4}-\d{4}$/,
      () => i18n.t("validation.cellphoneInvalid"),
    ),
  cpf: yup.string().required(() => i18n.t("validation.cpfRequired")).validateCpf(),
  term_accepted: yup
    .boolean()
    .isTrue(() => i18n.t("validation.termsRequired")),
});

export type RegisterInput = InferType<typeof registerSchemaInput>;

export async function register(input: RegisterInput): Promise<AxiosResponse> {
  const payload = {
    name: input.name,
    email: input.email.toLowerCase(),
    password: input.password,
    phone: input.phone,
    cellphone: input.cellphone,
    cep: input.cep,
    street: input.street,
    city: input.city,
    state: input.state,
    neighborhood: input.neighborhood,
    streetNumber: input.streetNumber,
    complement: input.complement,
    document: removeNotNumbers(String(input.cpf)),
    termAccepted: true,
  };

  const response = await api.post(`/api/v1/auth/signup`, payload);
  return response;
}

export async function checkDocument(document: string): Promise<DataCheckedCpf> {
  const response: AxiosResponse<DataCheckedCpf> = await axios.get(
    `/api/v1/users/check-document/${removeNotNumbers(String(document))}`,
  );
  return response.data;
}

export async function checkEmailApi(email: string): Promise<CheckEmailExist> {
  const response: AxiosResponse<CheckEmailExist> = await axios.get(
    `/api/v1/users/check-email/${email}`,
  );
  return response.data;
}
